import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, MessageSquareQuote } from "lucide-react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import Typography from "@/components/ui/typography";
import { useToast } from "@/hooks/use-toast";

import {
  deleteMarketplaceProfileTestimonial,
  getMarketplaceProfileTestimonials,
  MarketplaceTestimonialsKeys,
} from "../../api/marketplace-testimonials";
import AddTestimonialDialog from "./AddTestimonialDialog";
import TestimonialCard from "./TestimonialCard";

interface TestimonialsSectionProps {
  profileId: string;
}

const TestimonialsSection = ({ profileId }: TestimonialsSectionProps) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [deleteTestimonialId, setDeleteTestimonialId] = useState<string | null>(
    null,
  );

  const {
    data: testimonials = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: [MarketplaceTestimonialsKeys.GET_TESTIMONIALS, profileId],
    queryFn: () => getMarketplaceProfileTestimonials(profileId),
    enabled: !!profileId,
  });

  const { mutate: deleteTestimonial, isPending: isDeleting } = useMutation({
    mutationFn: (testimonialId: string) =>
      deleteMarketplaceProfileTestimonial(profileId, testimonialId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [MarketplaceTestimonialsKeys.GET_TESTIMONIALS, profileId],
      });
      toast({
        title: "Testimonial deleted",
        description: "The testimonial has been removed from your profile.",
      });
      setDeleteTestimonialId(null);
    },
    onError: () => {
      toast({
        title: "Failed to delete testimonial",
        description: "Please try again later.",
        variant: "destructive",
      });
    },
  });

  const handleDeleteClick = (testimonialId: string) => {
    setDeleteTestimonialId(testimonialId);
  };

  const handleConfirmDelete = () => {
    if (deleteTestimonialId) {
      deleteTestimonial(deleteTestimonialId);
    }
  };

  return (
    <div className="bg-[#f9f9f9] rounded-2xl p-6 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <MessageSquareQuote className="h-5 w-5 text-[#767677]" />
            <Typography size="l2" className="font-semibold text-[#494949]">
              Testimonials
            </Typography>
          </div>
          <Typography size="b2" className="text-[#767677]">
            Showcase testimonials from your satisfied clients on your profile.
          </Typography>
        </div>
        <div className="self-start sm:self-auto">
          <AddTestimonialDialog profileId={profileId} />
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      ) : error ? (
        <div className="text-center py-8">
          <Typography size="b2" className="text-red-500">
            Failed to load testimonials. Please try again later.
          </Typography>
        </div>
      ) : testimonials.length === 0 ? (
        <div className="text-center py-8 border-2 border-dashed border-[#e5e5e5] rounded-xl">
          <MessageSquareQuote className="h-10 w-10 text-[#d4d4d4] mx-auto mb-3" />
          <Typography size="l2" className="text-[#767677] mb-1">
            No testimonials yet
          </Typography>
          <Typography size="b2" className="text-[#a4a4a4]">
            Add your first testimonial to build credibility.
          </Typography>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {testimonials.map((testimonial) => (
            <TestimonialCard
              key={testimonial._id}
              testimonial={testimonial}
              onDelete={handleDeleteClick}
              isDeleting={isDeleting && deleteTestimonialId === testimonial._id}
            />
          ))}
        </div>
      )}

      <AlertDialog
        open={!!deleteTestimonialId}
        onOpenChange={(open) => !open && setDeleteTestimonialId(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Testimonial</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this testimonial? This action
              cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-red-500 hover:bg-red-600"
            >
              {isDeleting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                "Delete"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default TestimonialsSection;
